import axios from 'axios';
import config from '../config';
import {
  ACTIVITY_HEADER,
  readLastActivity,
  handleUnauthorized
} from '../utils/sessionGuard';

// API 기본 설정 - 사용자 API 서버 사용 (5001포트)
// config를 우선 사용하고, 없으면 기본값 사용
const getApiBaseUrl = () => {
  // config가 있으면 사용
  if (config && config.API_URL) {
    return config.API_URL;  // API_URL이 이미 /api를 포함함
  }
  
  // config가 없으면 기존 로직 사용
  const hostname = window.location.hostname;
  
  // localhost인 경우 로컬 API 사용
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://${hostname}:5101/api`;
  }
  
  // IP 주소로 접근하는 경우 - 상대 경로 사용
  if (hostname.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
    return '/api';  // Nginx가 프록시 처리
  }
  
  // 도메인으로 접근하는 경우 - 상대 경로 사용
  return '/api';  // Nginx가 프록시 처리
};

const API_BASE_URL = getApiBaseUrl();

// axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000 // 60초로 증가
});

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    /* [2026-09-07] 요청을 보낼 때 로그인 상태였는지 표시해 둔다.
       WHY: 401 을 받은 뒤에는 토큰이 이미 지워졌을 수 있어, 응답 시점에 다시 읽으면
         "비로그인 방문자의 401"과 "로그인 만료"를 구분할 수 없다. */
    config.__hadAuthToken = Boolean(token);
    /* [2026-09-07] 마지막 사람 입력 시각을 서버에 함께 보낸다(무활동 자동 로그아웃 판정용).
       기록이 없으면 헤더를 붙이지 않는다 = 서버는 자기가 아는 값만 쓴다(무손상). */
    const lastActivity = readLastActivity();
    if (lastActivity) {
      config.headers[ACTIVITY_HEADER] = String(lastActivity);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// [2026-11-01] 서버 응답 없이 끝난 요청의 회원용 안내 문구(아래 응답 인터셉터에서 쓴다)
const NO_RESPONSE_MESSAGES = {
  network: '서버에 연결하지 못했습니다. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.',
  timeout: '서버 응답이 늦어 요청 결과를 확인하지 못했습니다. 잠시 후 다시 확인해 주세요.',
};

// [2026-11-03] 파일로 받는 요청(상담 채팅 첨부)은 오류 본문도 파일 객체(Blob)로 온다 — 아래 처리가 사유 코드를 읽도록 JSON 이면 되돌린다.
//   WHY: 못 읽으면 자동 로그아웃·다른 곳 로그인 안내 없이 로그인 화면으로 간다.
const restoreJsonErrorBody = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob && data.type.includes('json')) {
    try { error.response.data = JSON.parse(await data.text()); } catch (_) { /* 못 읽으면 그대로 둔다 */ }
  }
};

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    await restoreJsonErrorBody(error);
    /* [2026-11-01] 서버 응답 없이 끝난 요청(연결 끊김·시간 초과)은 오류 문구를 한국어 안내로 바꾼다. 화면들은 이 문구를 그대로 알림창에 띄우는데
       (예: 게임 실행 실패) 종전에는 'Network Error'·'timeout of 60000ms exceeded' 같은 영문 기술 문구가 회원에게 보였다(2026-11-01 ur3 재측정).
       취소한 요청(ERR_CANCELED)은 알림 대상이 아니라 그대로 두고, 오류 종류(code)는 바꾸지 않는다(hooks/useDomainTheme.js 가 code 로 판정한다). */
    if (error && !error.response && error.code !== 'ERR_CANCELED') {
      error.message = (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') ? NO_RESPONSE_MESSAGES.timeout : NO_RESPONSE_MESSAGES.network;
    }
    // [쪽지/고객센터 분리] (2026-05-06) 게임 런치 미읽음 쪽지 게이트 응답 가로채기.
    //   user-api middleware/unreadNoticeGate.js 가 미읽음 notice 1건 이상 시 403 + code='UNREAD_NOTICE_GATE'(문구는 error 칸)
    //   응답을 보낸다. 클라이언트는 UnreadNoticeModal 강제 노출 이벤트를 발사.
    //   다른 라우트의 403 은 영향받지 않음(code 값 검사로 한정).
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'UNREAD_NOTICE_GATE'
    ) {
      try {
        window.dispatchEvent(new CustomEvent('force-unread-notice-modal', {
          detail: error.response.data
        }));
      } catch (_) { /* 모달 트리거 실패해도 원래 에러는 그대로 reject */ }
      return Promise.reject(error);
    }

    /* [2026-09-07] 로그인 만료 처리 — 규칙은 utils/sessionGuard.js 한 곳에 있다.
       요청 당시 토큰이 있었고(=로그인 상태였고) 로그인·회원가입·인증상태 조회가 아니면,
       지금 어느 화면에 있든 로그인 정보를 지우고 로그인 화면으로 보낸다.
       안내 문구는 사유별로 출처가 다르다 — 중복 로그인(SESSION_EXPIRED)은 종전 문구,
       무활동(IDLE_TIMEOUT)은 서버 문구, 토큰 만료는 만료 문구(utils/sessionGuard.js MESSAGES).
       토큰 없이 받은 401(비로그인 방문자의 공개 조회 등)은 종전처럼 무시한다.
       WHY: 종전에는 홈·슬롯·카지노 등 공개 화면에 있으면 아무 처리도 하지 않아, 상단바는
         로그인 상태인데 잔액·쪽지·게임 실행만 전부 실패하는 상태로 남았다(정본 SPEC §0). */
    if (error.response?.status === 401) {
      handleUnauthorized({
        hadToken: error.config?.__hadAuthToken === true,
        requestUrl: error.config?.url || '',
        data: error.response?.data
      });
    }
    return Promise.reject(error);
  }
);

// 공지사항 관련 API
export const noticeAPI = {
  // 공지사항 목록 조회
  getNotices: async (params = {}) => {
    try {
      const response = await api.get('/notices', { params });
      return response.data;
    } catch (error) {
      console.error('공지사항 목록 조회 실패:', error);
      throw error;
    }
  },

  // 공지사항 상세 조회
  getNoticeDetail: async (id) => {
    try {
      const response = await api.get(`/notices/${id}`);
      return response.data;
    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
      throw error;
    }
  },

  // 고정 공지사항 목록 조회
  getPinnedNotices: async () => {
    try {
      const response = await api.get('/notices/pinned/list');
      return response.data;
    } catch (error) {
      console.error('고정 공지사항 조회 실패:', error);
      throw error;
    }
  },

  // 슬라이더용 최신 공지사항 조회
  getSliderNotices: async () => {
    try {
      const response = await api.get('/notices/latest/slider');
      return response.data;
    } catch (error) {
      console.error('슬라이더 공지사항 조회 실패:', error);
      throw error;
    }
  },

  // 공지사항 읽음 처리
  markNoticeAsRead: async (id) => {
    try {
      const response = await api.post(`/notices/${id}/mark-read`);
      return response.data;
    } catch (error) {
      console.error('공지사항 읽음 처리 실패:', error);
      // 실패해도 기능에 영향 없도록 에러를 조용히 처리
      return { success: false };
    }
  },

  // 읽지 않은 공지사항 개수 조회
  getUnreadCount: async () => {
    try {
      const response = await api.get('/notices/unread-count');
      return response.data;
    } catch (error) {
      console.error('읽지 않은 공지사항 개수 조회 실패:', error);
      return { success: false, data: { unreadCount: 0 } };
    }
  }
};

export default api;